import React, { type JSX, useEffect } from 'react';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import type { ProductVoucher } from '@/domainLogic/types/typesProduct.ts';
import { createVoucherInfoClickedPayload } from '@/app/common/starTrack/events/voucherInfoClicked.ts';
import { createVoucherInfoCodeCopiedPayload } from '@/app/common/starTrack/events/voucherInfoCodeCopied.ts';
import BasicModalFooter from '@/app/components/search/components/Modal/LayoutElements/ModalFooter';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import { Cta } from '@/app/components/search/components/Modal/LayoutElements/Cta/styled.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { BRAND_IMAGES, getBrandImageUrl } from '@/app/common/contentHubAssets.ts';
import { preventEvents } from '@/app/components/search/utils/eventListeners.ts';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { Icon } from '@/app/common/icons/Icon';
import { CodeContainer, CodeText, Container, CopiedText, Description, Terms, Title, VoucherImage } from './styled';

type VoucherModalProps = {
  isOpen: boolean;
  onClose: () => void;
  voucher: ProductVoucher;
};

const resetCopiedState = (copied: boolean, setCopied: React.Dispatch<React.SetStateAction<boolean>>) => {
  if (copied) {
    const timer = setTimeout(() => {
      setCopied(false);
    }, 5000); // Reset copied state after 2 seconds

    return () => clearTimeout(timer); // Cleanup the timer on unmount
  }

  return () => {}; // Cleanup the timer on unmount
};

const VoucherModal = ({ isOpen, onClose, voucher }: VoucherModalProps): JSX.Element => {
  const { localization, searchInfoData } = usePageWrapperData();
  const { isMobile, starTrack, isEvoResultsLayout: isEvo } = useCommonClient();
  const [isCopied, setIsCopied] = React.useState(false);
  const { t } = useTranslation();

  const { searchId } = searchInfoData;
  const voucherIcon = getBrandImageUrl(BRAND_IMAGES.VOUCHER, localization);

  useEffect(() => {
    if (isOpen) {
      starTrack?.voucherInfoClicked(createVoucherInfoClickedPayload({ searchId }));
    }
  }, [isOpen]);

  const handleClickCopy = (e: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    if (isCopied) return; // Prevent copying if already copied

    e.stopPropagation();
    navigator.clipboard
      .writeText(voucher.code)
      .then(() => {
        setIsCopied(true);

        starTrack?.voucherInfoCodeCopied(createVoucherInfoCodeCopiedPayload({ searchId, voucher }));
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
      });
  };

  useEffect(() => {
    resetCopiedState(isCopied, setIsCopied);
  }, [isCopied]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div onClick={preventEvents}>
      <OpenXModal
        isOpen={isOpen}
        onClose={onClose}
        fullScreen={false}
        className={isEvo ? 'voucher-modal-evo' : 'voucher-modal'}
        size={MODAL_SIZES.S}
        // MPH-1116: the Evo side inset follows the other modals — 24px on the desktop dialog, where the
        // vertical stays at 0 because the sticky header and footer supply it. The sheet is left to the
        // OpenXModal default (16px all round, which is what it rendered before): the mobile spacing was
        // never part of the bug, and this body has no vertical padding of its own to fall back on.
        bodyPadding={isEvo ? { hor: 3, ver: 0 } : 2}
        id="voucher-modal"
        bodyBackgroundColor="backgroundPage"
        isMobile={isMobile}
        aria-label={voucher.title}
        brand={localization.brand}
        header={{
          sticky: isEvo,
          ...(isEvo && { element: <ModalHeader handleClose={onClose}>{null}</ModalHeader> }),
        }}
        footer={{
          sticky: true,
          element: (
            <BasicModalFooter
              handleApply={() => onClose()}
              applyText={t('vacation.oss.voucher.modal.cta')}
              layout="full-width"
              css={`
                ${Cta} {
                  width: 100%;
                }
              `}
            />
          ),
        }}
      >
        <Container>
          <VoucherImage src={voucherIcon} />
          <Title>{voucher.title}</Title>
          <Description>{voucher.description}</Description>
          <CodeContainer onClick={handleClickCopy}>
            <CodeText>
              {t('vacation.oss.voucher.modal.code')} <strong>{voucher.code}</strong>
            </CodeText>
            {isCopied ? (
              <CopiedText>
                <Icon category="Semantics" name="icon-check" size={16} color="iconPrimary" alt="Copied" />
                {t('vacation.oss.voucher.modal.copied')}
              </CopiedText>
            ) : (
              <Icon category="UI Essentials" name="icon-copy" size={20} color="iconPrimary" alt="" />
            )}
          </CodeContainer>
          <Terms href={voucher.tcUrl} target="_blank" rel="noopener noreferrer">
            {t('vacation.oss.voucher.modal.terms')}
          </Terms>
        </Container>
      </OpenXModal>
    </div>
  );
};

export default VoucherModal;
